import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import {
  ChevronRight,
  Heart,
  Compass,
  ShieldCheck,
  Sprout,
  ArrowRight,
  PawPrint,
  Code2,
  Users,
} from "lucide-react";
import { useLeadCapture } from "@/context/LeadCaptureContext";

const VALUES = [
  {
    icon: Heart,
    title: "Hundälskare först",
    desc: "Vi bygger för att hundarnas vardag ska bli bättre — och för att människorna runt dem ska få mer tid med det de älskar.",
  },
  {
    icon: Compass,
    title: "Hellre enkelt än fancy",
    desc: "Ett affärssystem ska sänka tröskeln, inte höja den. Vi designar varje funktion för att kunna användas av vem som helst på första försöket.",
  },
  {
    icon: ShieldCheck,
    title: "Trygghet i grunden",
    desc: "All data lagras i Sverige enligt GDPR. Vi tar dataintegritet och driftsäkerhet på största allvar — det är grunden i ett seriöst affärssystem.",
  },
  {
    icon: Sprout,
    title: "Bygg långsiktigt",
    desc: "Vi vill vara den långsiktiga partnern för Sveriges hunddagis, hundpensionat och hundrastare. Inte ett trendigt verktyg som försvinner efter två år.",
  },
];

const STATS = [
  { value: "500+", label: "Hundverksamheter i Sverige" },
  { value: "12 000+", label: "Hundar i systemet" },
  { value: "100%", label: "Svensk datalagring" },
  { value: "4.8 ⭐", label: "Snittbetyg från kunder" },
];

const PILLARS = [
  {
    icon: PawPrint,
    title: "Affärssystem för hundverksamheter",
    desc: "Vi gör det enkelt att driva hundpensionat, hunddagis och hundrastnings­verksamhet — från bokning till faktura — i ett enda modernt verktyg.",
  },
  {
    icon: Users,
    title: "Bättre upplevelse för hundägare",
    desc: "Med digitala hundprofiler, bildflöden och realtidsuppdateringar får hundägaren en service som ingen Excel-pärm någonsin kan matcha.",
  },
  {
    icon: Code2,
    title: "Modern teknik, lokal förståelse",
    desc: "Byggt i Sverige för svenska regler, svenska kundbeteenden och svenska bokföringssystem. Inte ett amerikanskt verktyg som översatts på ytan.",
  },
];

export default function AboutPage() {
  const { openLeadCapture } = useLeadCapture();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="page-about"
      className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100"
    >
      <Seo
        title="Om oss"
        description="Hundify är det moderna affärssystemet för Sveriges hunddagis, hundpensionat och hundrastnings­verksamheter. Lär dig mer om vår mission och våra värderingar."
        path="/om-oss"
      />
      <Navbar />
      <main className="pt-28">
        {/* Breadcrumb */}
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <nav className="flex items-center text-xs text-slate-500 dark:text-slate-400 gap-1">
            <Link to="/" className="hover:text-emerald-600 dark:hover:text-emerald-400">
              Hem
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-700 dark:text-slate-200">Om oss</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="relative overflow-hidden pt-16 pb-20 md:pt-20 md:pb-24">
          <div className="absolute -top-24 -left-20 h-[420px] w-[420px] blob-emerald opacity-60 pointer-events-none" />
          <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-emerald-700 dark:text-emerald-300">
                Om Hundify
              </div>
              <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] font-bold tracking-tight text-slate-900 dark:text-white">
                Det moderna{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">affärssystemet</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-emerald-300/60 dark:bg-emerald-500/30 -z-0 rounded" />
                </span>{" "}
                för Sveriges hundverksamheter.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-slate-600 dark:text-slate-300 max-w-xl">
                Hundify är byggt för att effektivisera vardagen för dig som driver
                hunddagis, hundpensionat eller hundrastnings­verksamhet. Vårt mål
                är enkelt: mindre administration, fler nöjda kunder och fler glada
                hundar.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => openLeadCapture("Om oss")}
                  data-testid="about-primary-cta"
                  className="h-12 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-[0_8px_22px_-4px_rgba(16,185,129,0.55)] hover:-translate-y-0.5 transition-all"
                >
                  Boka en demo <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Link to="/pricing">
                  <Button
                    variant="outline"
                    className="h-12 px-6 rounded-xl border-slate-300 dark:border-slate-700 font-semibold"
                  >
                    Se priser
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-[28px] overflow-hidden aspect-[4/5] shadow-2xl ring-1 ring-slate-200 dark:ring-slate-800">
                <img
                  src="https://images.pexels.com/photos/9455270/pexels-photo-9455270.jpeg"
                  alt="Hundar i naturen"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
              </div>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-20 bg-slate-50 dark:bg-slate-900/40 border-y border-slate-200 dark:border-slate-800">
          <div className="mx-auto max-w-7xl px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
                Vår mission
              </div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold leading-tight text-slate-900 dark:text-white">
                Vi vill att svenska hundverksamheter ska kunna driva sin verksamhet på 2030-talet — inte 2010-talet.
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-5 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              <p>
                Att driva ett hunddagis eller hundpensionat i Sverige idag innebär
                ofta en blandning av papperslappar, Excel-ark, SMS-konversationer
                och tre olika system som inte pratar med varandra. Det är inte
                bara ineffektivt — det skapar misstag, stress och förlorade
                intäkter.
              </p>
              <p>
                Vi byggde Hundify för att lösa det. Ett modernt, molnbaserat
                affärssystem som samlar bokning, beläggning, fakturering, kund­
                kommunikation och hundjournaler i ett snyggt verktyg. Resultatet:
                våra kunder sparar i snitt 12–18 timmar i månaden — tid som går
                tillbaka till hundarna.
              </p>
              <p>
                Vår övertygelse är enkel: när en hundverksamhet effektiviseras,
                vinner alla. Personalen, kunderna och framför allt hundarna.
              </p>
            </div>
          </div>
        </section>

        {/* What we build */}
        <section className="py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
                Vad vi bygger
              </div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                Tre tydliga åtaganden.
              </h2>
            </div>
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              {PILLARS.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-7 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:border-emerald-500/40"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display mt-5 text-xl font-semibold text-slate-900 dark:text-white">
                    {title}
                  </h3>
                  <p className="mt-3 text-slate-600 dark:text-slate-300 leading-relaxed">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-slate-900 text-white">
          <div className="mx-auto max-w-7xl px-6 lg:px-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-display text-5xl font-bold text-emerald-400">{s.value}</div>
                <div className="mt-2 text-sm text-slate-300 uppercase tracking-wider">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Values */}
        <section className="py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 dark:text-emerald-400">
                Våra värderingar
              </div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                Det här styr varje beslut vi tar.
              </h2>
            </div>
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              {VALUES.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="flex gap-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-7"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-md">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-white">
                      {title}
                    </h3>
                    <p className="mt-2 text-slate-600 dark:text-slate-300 leading-relaxed">
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 text-white p-10 md:p-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-dotted opacity-20" />
              <div className="relative max-w-2xl">
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                  Vill du också effektivisera din hundverksamhet?
                </h2>
                <p className="mt-4 text-slate-300 text-lg">
                  Boka en kostnadsfri demo. Vi visar systemet, svarar på frågor
                  och hjälper dig avgöra om Hundify passar dig.
                </p>
                <Button
                  onClick={() => openLeadCapture("Om oss — bottom CTA")}
                  data-testid="about-bottom-cta"
                  className="mt-8 h-12 px-6 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-[0_8px_22px_-4px_rgba(16,185,129,0.55)]"
                >
                  Boka demo gratis <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
